// Use always version 3.2.8, new version has different filter logic.
import { useMemo } from 'react'
import { useTimezoneSelect } from 'react-timezone-select'

import {
  COMMON_TIMEZONE_VALUES,
  ETimezoneGroup,
  TIMEZONE_GROUP_ORDER,
  USA_CANADA_TIMEZONES,
} from '~/features/agents/hooks/useTimezone/constants'
import timezones from '~/models/timezones'

const TIMEZONES = timezones
  .flatMap(({ utc }) => utc)
  .reduce((acc, curr) => ({ ...acc, [curr]: curr }), {})

export type TTimezoneOption = {
  value: string
  label: string
  group: ETimezoneGroup
  abbrev?: string
  altName?: string
  offset?: number
}

const getTimezoneGroup = (timezoneValue: string): ETimezoneGroup => {
  if (USA_CANADA_TIMEZONES.has(timezoneValue)) {
    return ETimezoneGroup.usaCanada
  }

  const [region] = timezoneValue.split('/')

  if (region === 'Europe') {
    return ETimezoneGroup.europe
  }

  if (
    region === 'Asia' ||
    region === 'Pacific' ||
    region === 'Australia' ||
    region === 'Indian'
  ) {
    return ETimezoneGroup.asiaPacific
  }

  return ETimezoneGroup.other
}

function useTimezone() {
  const { options } = useTimezoneSelect({
    labelStyle: 'original',
    timezones: TIMEZONES,
  })

  const groupedOptions = useMemo<TTimezoneOption[]>(() => {
    const optionsByValue = new Map(
      options.map((option) => [option.value, option]),
    )
    const commonValuesSet = new Set<string>(COMMON_TIMEZONE_VALUES)

    const commonOptions = COMMON_TIMEZONE_VALUES.reduce<TTimezoneOption[]>(
      (acc, value) => {
        const option = optionsByValue.get(value)

        if (option) {
          acc.push({ ...option, group: ETimezoneGroup.commonlyUsed })
        }

        return acc
      },
      [],
    )

    const remainingOptions = options
      .filter((option) => !commonValuesSet.has(option.value))
      .map((option) => ({
        ...option,
        group: getTimezoneGroup(option.value),
      }))
      .sort(
        (a, b) =>
          TIMEZONE_GROUP_ORDER.indexOf(a.group) -
          TIMEZONE_GROUP_ORDER.indexOf(b.group),
      )

    return [...commonOptions, ...remainingOptions]
  }, [options])

  return { options: groupedOptions }
}

export default useTimezone
